import { HighlightedFields, PlaceDetails } from '@/map/domain/entities/map.types';

import FeatureMarker from '@/map/presentation/components/markers/FeatureMarker';
import { GoogleMapsService } from '@/map/application/services/googleMapsService';
import { HandleMapInteractions } from '@/map/application/useCases/HandleMapInteractions';
import { LocationFormPanel } from './LocationFormPanel';
import Map from '@/map/presentation/components/RaptorMap/RaptorMap';
import { MapRepository } from '@/map/infrastructure/repositories/MapRepository';
import { Menu, MenuItem, Paper, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import { useMap } from '@vis.gl/react-google-maps';
import tid from '@/common/infrastructure/config/tid';

interface MapContentProps {
  onPlaceSelect: (place: google.maps.places.Place) => void;
  setMarkerPosition: (position: google.maps.LatLngLiteral) => void;
  selectedPlaceDetails: PlaceDetails | null;
  setSelectedPlaceDetails: (details: PlaceDetails | null) => void;
  markerPosition: google.maps.LatLngLiteral | null;
  selectedPlace: google.maps.places.Place | null;
  onMarkerDragEnd: (position: google.maps.LatLngLiteral) => void;
  highlightedFields?: HighlightedFields;
  updateFormValues: (details: PlaceDetails) => Promise<void>;
  hideCoordinates?: boolean;
  hideCountry?: boolean;
}

export const MapContent: React.FC<MapContentProps> = ({
  onPlaceSelect,
  setMarkerPosition,
  selectedPlaceDetails,
  setSelectedPlaceDetails,
  markerPosition,
  selectedPlace,
  onMarkerDragEnd,
  highlightedFields,
  updateFormValues,
  hideCoordinates = false,
  hideCountry = false
}) => {
  const mapRepository = new MapRepository();
  const handleMapInteractions = new HandleMapInteractions(mapRepository);
  const googleMapsService = React.useMemo(() => new GoogleMapsService(), []);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    latLng: google.maps.LatLngLiteral | null;
  } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1150));
  const map = useMap();

  // Helper function to pan with offset for small screens
  const panToWithOffset = React.useCallback(
    (position: google.maps.LatLngLiteral) => {
      const mapWidth = mapContainerRef.current?.clientWidth || window.innerWidth;
      googleMapsService.panToWithOffset(map, position, isSmallScreen, mapWidth);
    },
    [map, isSmallScreen, googleMapsService]
  );

  // Handle right-click on map using React component prop
  const handleRightClick = (e: MapMouseEvent) => {
    e.domEvent?.preventDefault();

    if (e.detail.latLng && e.domEvent instanceof MouseEvent) {
      // Use viewport coordinates directly for menu positioning
      setContextMenu({
        mouseX: e.domEvent.clientX,
        mouseY: e.domEvent.clientY,
        latLng: e.detail.latLng
      });
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleSetLocation = () => {
    if (contextMenu?.latLng) {
      setMarkerPosition(contextMenu.latLng);
      onMarkerDragEnd(contextMenu.latLng);
      handleCloseContextMenu();
    }
  };

  // Adjust map center when marker position changes on small screens
  useEffect(() => {
    if (!map || !markerPosition) return;
    panToWithOffset(markerPosition);
  }, [map, isSmallScreen, markerPosition]);

  return (
    <Paper
      ref={mapContainerRef}
      id="map-container"
      sx={{
        height: '500px',
        minHeight: '500px',
        width: '100%',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        position: 'relative'
      }}
    >
      <LocationFormPanel
        onPlaceSelect={onPlaceSelect}
        setMarkerPosition={setMarkerPosition}
        selectedPlaceDetails={selectedPlaceDetails}
        setSelectedPlaceDetails={setSelectedPlaceDetails}
        markerPosition={markerPosition}
        highlightedFields={highlightedFields}
        updateFormValues={updateFormValues}
        hideCoordinates={hideCoordinates}
        hideCountry={hideCountry}
        panToWithOffset={panToWithOffset}
      />
      <div
        data-map-area
        role="application"
        aria-label="Map area"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        <Map
          style={{ width: '100%', height: '100%' }}
          handleMapInteractions={handleMapInteractions}
          showControls={true}
          onContextmenu={handleRightClick}
        >
          {markerPosition && (
            <FeatureMarker
              featureId="selected-location"
              position={markerPosition}
              type="Jobsite"
              label={selectedPlace?.displayName ?? 'Selected Location'}
              onDragEnd={onMarkerDragEnd}
            />
          )}
        </Map>
      </div>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? {
                top: contextMenu.mouseY,
                left: contextMenu.mouseX
              }
            : undefined
        }
        slotProps={{
          root: {
            'aria-labelledby': 'context-menu'
          }
        }}
        sx={{
          zIndex: 99999, // Ensure it's above browser menu
          '& .MuiPaper-root': {
            zIndex: 99999,
            borderRadius: '0 8px 8px 8px'
          }
        }}
        disablePortal={false}
        disableScrollLock={true}
      >
        <MenuItem onClick={handleSetLocation} {...tid('map:context-menu:set-location-menu-item')}>
          Set location here
        </MenuItem>
      </Menu>
    </Paper>
  );
};
